import React, { useMemo } from "react"
import SubsectionContainer from "./SubsectionContainer"
import { compact, get, isEmpty, map } from "lodash"
import { Provider } from "../../../domain/Provider/schema"
import { SubsectionContent, SubsectionContentList } from "./SubsectionContent"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import { useLazyCmsCollectionMap } from "../../../domain/CMS/useLazyCmsCollectionMap"

interface SpecialitiesContentProps {
  providers: Provider[]
}

interface SpecialitiesData {
  primary: Record<string, boolean>
  additional: Record<string, boolean>
}
const coalateSpecialitiesInfo = (providers: Provider[]) => {
  const specialitiesData: SpecialitiesData = {
    primary: {},
    additional: {},
  }
  for (const provider of providers) {
    if (isEmpty(provider)) {
      continue
    }
    // use map to handle dupes
    if (provider.providerSpecialties) {
      for (const specialty of provider.providerSpecialties) {
        if (specialty.specialtyTypeDisplayId === "primary") {
          specialitiesData.primary[specialty.specialtyDisplayId] = true
        }
        if (
          specialty.specialtyTypeDisplayId === "additional" &&
          !specialitiesData.primary[specialty.specialtyDisplayId]
        ) {
          specialitiesData.additional[specialty.specialtyDisplayId] = true
        }
      }
    }
  }
  return specialitiesData
}
export const SpecialitiesContent: React.FC<SpecialitiesContentProps> = ({
  providers,
}: SpecialitiesContentProps) => {
  const { cms: cmsContent, isReady: isCMSReady } = useCMSReady([
    "provider",
    "providerSpecialtiesMap",
  ])
  const providerText = cmsContent.provider
  const providerSpecialtiesMap = useLazyCmsCollectionMap(
    "specialties",
    "providerSpecialtiesMap",
  )

  const specialitiesData = useMemo(() => {
    return isEmpty(providers) ? null : coalateSpecialitiesInfo(providers)
  }, [providers])

  const specialitiesPanelsData = useMemo(() => {
    if (!isCMSReady || isEmpty(specialitiesData) || !providerSpecialtiesMap) {
      return []
    }

    // return the matching specialties data
    const mapAndCompact = (
      cmsMap: Record<string, string>,
      specialitiesIndex: string,
      shouldSort: boolean = false,
    ) => {
      const ret = compact(
        map(
          cmsMap,
          (label, key) =>
            get(specialitiesData, [specialitiesIndex, key], null) && label,
        ),
      )
      if (shouldSort) {
        ret.sort((a, b) => (a && b ? a.localeCompare(b) : 0))
      }

      return ret
    }

    return [
      {
        subTitle: providerText?.specialtiesPrimarySpecialtiesHeader,
        items: mapAndCompact(providerSpecialtiesMap, "primary"),
      },
      {
        subTitle: providerText?.specialtiesAdditionalHeader,
        items: mapAndCompact(providerSpecialtiesMap, "additional"),
      },
    ]
  }, [
    isCMSReady,
    specialitiesData,
    providerSpecialtiesMap,
    providerText?.specialtiesPrimarySpecialtiesHeader,
    providerText?.specialtiesAdditionalHeader,
  ])

  return (
    <SubsectionContainer>
      {specialitiesPanelsData.map(({ subTitle, items }, index) => {
        return items.length > 0 ? (
          <SubsectionContent key={index} title={subTitle}>
            <SubsectionContentList listItems={items} />
          </SubsectionContent>
        ) : null
      })}
    </SubsectionContainer>
  )
}
